import React, { useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useSelector } from 'Hooks';
import { canAccessPrivateMenuItems } from 'utils/userRoleUtils';

const Wrapper = styled.div<{ isSuperAdmin: boolean }>`
  position: relative;
  background-color: #fff;
  width: ${({ isSuperAdmin }) => (isSuperAdmin ? '108px' : '88px')};

  &:last-child {
    margin-top: auto;
    background-color: transparent;
  }
`;

const MenuItemWrapper = styled.div<{ active: boolean; inverted?: boolean; isSuperAdmin?: boolean }>`
  display: flex;
  flex-direction: ${({ isSuperAdmin }) => (isSuperAdmin ? 'row' : 'column')};
  flex-wrap: nowrap;
  justify-content: ${({ isSuperAdmin }) => (isSuperAdmin ? 'flex-start' : 'center')};
  gap: ${({ isSuperAdmin }) => (isSuperAdmin ? '4px' : '0')};
  align-items: center;
  padding: ${({ isSuperAdmin }) => (isSuperAdmin ? '4px 4px 4px 8px' : '12px 4px')};
  min-height: ${({ isSuperAdmin }) => (isSuperAdmin ? '40px' : '64px')};
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  height: 16px;
  letter-spacing: 0.24px;
  line-height: 16px;
  word-break: break-word;
  cursor: pointer;
  color: #1c232c;
  text-align: ${({ isSuperAdmin }) => (isSuperAdmin ? 'left' : 'center')};
  z-index: 3;
  position: relative;
  background-color: ${({ active }) => (active ? '#e8e9ea' : 'transparent')};
  transition: background-color 0.2s ease-in-out;

  ${({ inverted }) =>
    inverted &&
    `
    color: #fff;
    background-color: #232323;
    height: 85px;
  `}
`;

const Backdrop = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 1;
  left: 0;
`;

const Icon = styled.div`
  height: 20px;
  width: 20px;
  margin-bottom: 4px;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const List = styled.div`
  position: absolute;
  right: -8px;
  transform: translateX(100%);
  width: 192px;
  z-index: 2;
  top: 0;
  max-height: 290px;
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0px 0px 15px -7px rgb(0 0 0 / 32%), 0px 0px 46px 8px rgb(0 0 0 / 20%),
    0px 24px 38px 3px rgb(0 0 0 / 24%);
  background-color: #fbfbfb;
  display: flex;
  flex-flow: column nowrap;
  padding: 16px;
  animation: fade-in 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const ListItem = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 16px;
  color: #1c232c;
  margin-bottom: 16px;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }
`;

interface Props {
  id?: string;
  icon?: JSX.Element;
  name: string | null;
  to?: string;
  onClick?: () => void;
  listItems?: any;
  menuItemOpen?: string;
  active: boolean;
  handleClose?: () => void;
}

const MenuItem: React.FC<Props> = ({
  id,
  icon,
  name,
  to,
  onClick,
  listItems,
  menuItemOpen,
  active,
  handleClose,
}) => {
  const [outOfBounds, setOutOfBounds] = useState(false);
  const history = useHistory();
  const isMenuOpen = menuItemOpen == id;
  const listRef = useRef<HTMLDivElement>(null);
  const role = useSelector((state) => state.userRole);
  const isSuperAdmin = canAccessPrivateMenuItems(role);

  const handleListClick = (to: string) => {
    if (to) {
      history.push('/' + to);
      if (handleClose) {
        handleClose();
      }
    }
  };

  useEffect(() => {
    if (isMenuOpen) {
      const windowHeight = window.innerHeight;
      const bounds = listRef.current?.getBoundingClientRect();

      if (bounds && bounds.height + bounds.top > windowHeight) {
        setOutOfBounds(true);
      }
    } else {
      setOutOfBounds(false);
    }
  }, [isMenuOpen]);

  return (
    <Wrapper isSuperAdmin={isSuperAdmin}>
      <MenuItemWrapper
        inverted={id === 'insights'}
        onClick={onClick}
        active={active || isMenuOpen}
        isSuperAdmin={isSuperAdmin}>
        <Icon>{icon}</Icon>
        {name}
      </MenuItemWrapper>

      {isMenuOpen && listItems && (
        <List ref={listRef} style={outOfBounds ? { top: 'auto', bottom: '0' } : {}}>
          {listItems.map((item, i) => (
            <ListItem key={i} onClick={() => handleListClick(item[0])}>
              {item[1]}
            </ListItem>
          ))}
        </List>
      )}
      {isMenuOpen && <Backdrop onClick={handleClose} />}
    </Wrapper>
  );
};

export default MenuItem;
